import { MISSIONS, type Threat } from '../../data/missions';
import { WORLD_IDS, parseLevelId, type LevelId, type WorldId } from '../../data/ids';
import { LEVELS } from '../../data/levels';
import { t, type StringKey } from '../../i18n/es';
import type { SaveSlot } from '../../save/schema';
import { formatNumber } from '../format';

export type MissionStatus = 'BLOQUEADA' | 'DISPONIBLE' | 'COMPLETADA';

export interface MissionRow {
  levelId: LevelId;
  op: string;
  name: string;
  threat: string;
  status: MissionStatus;
  /** Best score of a completed operation, "4.210". */
  best: string | null;
  /** The best score was made with the assist mode on (#138). */
  assisted: boolean;
  world: WorldId;
  /** An unlocked operation whose level is not built yet: shown as available with the coming-soon note (#101). */
  comingSoon: boolean;
}

export interface WorldGroup {
  world: WorldId;
  name: string;
  /** Its three operations are completed (GDD §10.1, #101). */
  completed: boolean;
}

const THREAT_KEYS: Record<Threat, StringKey> = {
  LOW: 'ui.misiones.threat.low',
  MEDIUM: 'ui.misiones.threat.medium',
  HIGH: 'ui.misiones.threat.high',
  CRITICAL: 'ui.misiones.threat.critical',
};

/**
 * The 12 rows of MISIONES for a slot (GDD §13.2, §10.1): COMPLETADA with its best score when completed
 * (even if the data forgot to unlock it), DISPONIBLE when unlocked, BLOQUEADA otherwise. The unlocking
 * rule itself belongs to #41; this only reads the slot.
 */
export function missionRows(slot: SaveSlot | null, levels: Readonly<Record<string, unknown>> = LEVELS): MissionRow[] {
  return MISSIONS.map((mission) => {
    const record = slot?.completedLevels[mission.levelId];
    const unlocked = slot?.unlockedLevels.includes(mission.levelId) ?? mission.levelId === '1-1';
    const status: MissionStatus = record ? 'COMPLETADA' : unlocked ? 'DISPONIBLE' : 'BLOQUEADA';
    return {
      levelId: mission.levelId,
      op: mission.op,
      name: t(`mission.${mission.levelId}.name` as StringKey).toLocaleUpperCase('es-ES'),
      threat: t(THREAT_KEYS[mission.threat]),
      status,
      best: record ? formatNumber(record.bestScore) : null,
      assisted: record?.assisted ?? false,
      world: parseLevelId(mission.levelId).world,
      comingSoon: status === 'DISPONIBLE' && !levels[mission.levelId],
    };
  });
}

/** The four worlds of the campaign with their name and whether they are completed (#101). */
export function worldGroups(rows: readonly MissionRow[]): WorldGroup[] {
  return WORLD_IDS.map((world) => ({
    world,
    name: t(`ui.misiones.world.${world}`),
    completed: rows.filter((row) => row.world === world).every((row) => row.status === 'COMPLETADA'),
  }));
}

/** The operation to play next: the last available one, else the first row. */
export function nextMissionIndex(rows: readonly MissionRow[]): number {
  const index = rows.map((row) => row.status).lastIndexOf('DISPONIBLE');
  return index >= 0 ? index : 0;
}
